import { createBrowserRouter, Navigate, Outlet } from 'react-router-dom';
import { Suspense, lazy, type ReactNode } from 'react';
import { RootLayout } from './components/RootLayout';
import { AuthGate } from './components/AuthGate';

const LandingPage = lazy(() => import('./routes/LandingPage'));
const PublicPage = lazy(() => import('./routes/PublicPage'));
const LoginPage = lazy(() => import('./routes/LoginPage'));
const SignupPage = lazy(() => import('./routes/SignupPage'));
const MfaChallengePage = lazy(() => import('./routes/MfaChallengePage'));
const LinksPage = lazy(() => import('./routes/LinksPage'));
const ConfigPage = lazy(() => import('./routes/ConfigPage'));
const AccountPage = lazy(() => import('./routes/AccountPage'));
const MaintenancePage = lazy(() => import('./routes/MaintenancePage'));
const ContactsPage = lazy(() => import('./routes/ContactsPage'));
const ManageSetsPage = lazy(() => import('./routes/ManageSetsPage'));
const ImportExportPage = lazy(() => import('./routes/ImportExportPage'));
const PasswordResetPage = lazy(() => import('./routes/PasswordResetPage'));
const PasswordResetConfirmPage = lazy(() => import('./routes/PasswordResetConfirmPage'));
const TiersPage = lazy(() => import('./routes/TiersPage'));
const BillingReturnPage = lazy(() => import('./routes/BillingReturnPage'));
const BillingCancelPage = lazy(() => import('./routes/BillingCancelPage'));

function withSuspense(el: ReactNode): ReactNode {
  return <Suspense fallback={<div className="sl-loading">Loading…</div>}>{el}</Suspense>;
}

export const router = createBrowserRouter([
  {
    element: (
      <RootLayout>
        <Outlet />
      </RootLayout>
    ),
    children: [
      // Public routes - no AuthGate. Landing redirects authed users to /links.
      { path: '/', element: withSuspense(<LandingPage />) },
      { path: '/p/:slug', element: withSuspense(<PublicPage />) },
      { path: '/login', element: withSuspense(<LoginPage />) },
      { path: '/signup', element: withSuspense(<SignupPage />) },
      { path: '/mfa', element: withSuspense(<MfaChallengePage />) },
      { path: '/password-reset', element: withSuspense(<PasswordResetPage />) },
      { path: '/password-reset/confirm', element: withSuspense(<PasswordResetConfirmPage />) },
      { path: '/maintenance', element: withSuspense(<MaintenancePage />) },
      {
        element: <AuthGate><Outlet /></AuthGate>,
        children: [
          { path: '/links', element: withSuspense(<LinksPage />) },
          { path: '/links/:groupSlug', element: withSuspense(<LinksPage />) },
          { path: '/links/:groupSlug/:setSlug', element: withSuspense(<LinksPage />) },
          { path: '/config', element: withSuspense(<ConfigPage />) },
          { path: '/account', element: withSuspense(<AccountPage />) },
          { path: '/contacts', element: withSuspense(<ContactsPage />) },
          { path: '/contacts/:tab', element: withSuspense(<ContactsPage />) },
          { path: '/manage-sets', element: withSuspense(<ManageSetsPage />) },
          { path: '/manage-sets/:groupSlug', element: withSuspense(<ManageSetsPage />) },
          { path: '/import-export', element: withSuspense(<ImportExportPage />) },
          { path: '/tiers', element: withSuspense(<TiersPage />) },
          { path: '/billing/return', element: withSuspense(<BillingReturnPage />) },
          { path: '/billing/cancel', element: withSuspense(<BillingCancelPage />) },
        ],
      },
      { path: '*', element: <Navigate to="/links" replace /> },
    ],
  },
]);
